import type {Printout} from "crqlar-react-common/dist/models/printout/printout.model";
import {getPrintoutLanguages} from "../../core/printoutDocument";

const SELECTION_SEPARATOR = ":";

export interface PrintoutLayoutSelection {
    language: string;
    printoutId: string;
}

export interface PrintoutLayoutOption {
    group: string;
    label: string;
    selectedLabel: string;
    value: string;
}

export const serializePrintoutLayoutSelection = (
    {language, printoutId}: PrintoutLayoutSelection,
) => (
    `${encodeURIComponent(printoutId)}${SELECTION_SEPARATOR}${encodeURIComponent(language)}`
);

export const parsePrintoutLayoutSelection = (value?: string): PrintoutLayoutSelection | undefined => {
    if (!value) return undefined;

    const separatorIndex = value.indexOf(SELECTION_SEPARATOR);
    if (separatorIndex <= 0 || separatorIndex === value.length - 1) return undefined;

    const printoutId = decodeURIComponent(value.slice(0, separatorIndex));
    const language = decodeURIComponent(value.slice(separatorIndex + 1));

    return printoutId && language ? {language, printoutId} : undefined;
};

export const getPrintoutLayoutSelectionValue = (
    printout?: Printout,
    language?: string,
) => (
    printout?.id && language
        ? serializePrintoutLayoutSelection({language, printoutId: printout.id})
        : undefined
);

export const getPrintoutLayoutOptions = (
    printouts: Printout[],
    formatLanguage: (language: string) => string,
    untitledLabel: string,
): PrintoutLayoutOption[] => {
    const printoutsByLanguage = new Map<string, Printout[]>();

    printouts.forEach((printout) => {
        getPrintoutLanguages(printout).forEach((language) => {
            const group = printoutsByLanguage.get(language) ?? [];
            group.push(printout);
            printoutsByLanguage.set(language, group);
        });
    });

    return Array.from(printoutsByLanguage.entries()).flatMap(([language, languagePrintouts]) => {
        const languageLabel = formatLanguage(language);

        return languagePrintouts.flatMap((printout) => {
            if (!printout.id) return [];

            const printoutLabel = printout.name?.trim() || untitledLabel;

            return [{
                group: languageLabel,
                label: printoutLabel,
                selectedLabel: `${printoutLabel} · ${languageLabel}`,
                value: serializePrintoutLayoutSelection({
                    language,
                    printoutId: printout.id,
                }),
            }];
        });
    });
};
